import React, { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form-v7';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';

import { EmployeeFormProps, EmployeeFormData } from '@personnel/presentation/types';

import { NotificationsSection } from '@personnel/presentation/components/form/EmployeeForm/NotificationsSection';
import { EmployeeGeneralForm } from '@personnel/presentation/components/form/EmployeeForm/EmployeeGeneralForm';
import { EmployeeFormFooter } from '@personnel/presentation/components/form/EmployeeForm/EmployeeFormFooter';
import { styles } from '@personnel/presentation/components/form/EmployeeForm/shared';

export const EmployeeForm = ({
  onSubmit,
  form,
  notificationConfig,
  saveAction,
  isSubmitting,
  cancelPath,
  isEditMode,
  isAccountInactive,
}: EmployeeFormProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const [activeTab, setActiveTab] = useState<'general' | 'notifications'>('general');
  const methods: UseFormReturn<EmployeeFormData> = form;

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (onSubmit) {
      await onSubmit(data);
    }
  });

  return (
    <div className={styles.form}>
      <form className={styles.form} id="employee-form" onSubmit={handleSubmit}>
        <Tabs
          onValueChange={(value) => {
            const scope = value as 'general' | 'notifications';
            setActiveTab(scope);
            methods.setValue('scope', scope);
          }}
          value={activeTab}
        >
          <TabsList className={styles.tabsList}>
            <TabsTrigger className={styles.tabsTrigger} value="general">
              {t('form.generalTab')}
            </TabsTrigger>
            <TabsTrigger className={styles.tabsTrigger} value="notifications">
              {t('form.notificationsTab')}
            </TabsTrigger>
          </TabsList>

          <TabsContent className={styles.tabsContent} value="general">
            <EmployeeGeneralForm form={methods} isAccountInactive={isAccountInactive} isEditMode={isEditMode} />
          </TabsContent>

          <TabsContent className={cn(styles.tabsContent)} value="notifications">
            <NotificationsSection form={methods} notificationConfig={notificationConfig} />
          </TabsContent>
        </Tabs>
      </form>

      <EmployeeFormFooter cancelPath={cancelPath} isSubmitting={isSubmitting} saveAction={saveAction} />
    </div>
  );
};
